import { useEffect, useState } from 'react';
import { isUsableSlug, HALL_LIMITS, SocialAuthError } from '@versesverse/core';
import type { HallView, NewHall } from '@versesverse/core';
import { useLang } from '../i18n/lang';
import { refusalWords } from '../i18n/refusal';

/** What the halls panel reads off the social client. */
export interface HallsPanelClient {
  listHalls(): Promise<HallView[]>;
  myHalls(): Promise<HallView[]>;
  createHall(hall: NewHall): Promise<HallView>;
  signInUrl(): string;
}

export interface HallsPanelProps {
  client: HallsPanelClient;
  /** Whether anyone is signed in — "yours" and the create form need a reader. */
  signedIn: boolean;
  onOpen(slug: string): void;
  onClose(): void;
}

/** A name → a usable address, so nobody has to understand what a slug is. */
export function slugFrom(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, HALL_LIMITS.slugMaxLength)
    .replace(/-+$/, '');
}

/*
 * WHERE HALLS ARE FOUND AND MADE.
 *
 * Without this the Hall feature was unreachable: the page existed, the API existed, and nothing in
 * the app could open one or create one. A leader could only arrive at `?hall=<slug>` if somebody
 * had told them the address.
 *
 * The address is DERIVED from the name rather than asked for, because "slug" is our word and not a
 * leader's — they are naming their community, not choosing a URL path. It stays editable, since the
 * derived one may be taken and the server says so in words this shows verbatim.
 */
export function HallsPanel({ client, signedIn, onOpen, onClose }: HallsPanelProps): React.JSX.Element {
  const { t } = useLang();
  const [halls, setHalls] = useState<HallView[]>([]);
  const [unread, setUnread] = useState(false);
  const [mineUnread, setMineUnread] = useState(false);
  const [mine, setMine] = useState<HallView[]>([]);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  /** Once a leader edits the address themselves, stop overwriting it from the name. */
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  /** Whether the new Hall goes into the directory. Checked by default — see the control below. */
  const [listed, setListed] = useState(true);

  useEffect(() => {
    let live = true;
    /*
     * NOT AN EMPTY LIST, and the second one is the worse claim: "You are not in a hall yet" is
     * a statement about the reader's own membership, made because a request failed.
     */
    client.listHalls()
      .then(found => { if (live) { setHalls(found); setUnread(false); } })
      .catch(() => { if (live) setUnread(true); });
    if (signedIn) {
      client.myHalls()
        .then(found => { if (live) { setMine(found); setMineUnread(false); } })
        .catch(() => { if (live) setMineUnread(true); });
    }
    return () => { live = false; };
  }, [client, signedIn]);

  const open = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const wanted = name.trim();
    const address = (slugTouched ? slug : slugFrom(wanted)).trim();
    if (!wanted || busy) return;

    /*
     * AND AN ADDRESS THAT CANNOT BE MADE IS SAID OUT LOUD, rather than returning quietly.
     *
     * The derivation keeps lowercase letters, digits and hyphens and drops everything else, so a
     * community named in Hebrew or in Arabic derived to an EMPTY string — and this returned, with
     * the button still live and nothing on screen. The leader pressed it and the product did
     * nothing at all: no message, no request, no reason. In a product that reads in three
     * languages, at the first act a community leader ever performs.
     *
     * The address field is right there and takes whatever they type, so the fix is to say which
     * half is missing rather than to invent one for them: a transliteration would choose a Latin
     * name for somebody else's community, and a generated one would put a string nobody picked in
     * the URL they are about to hand to their people.
     */
    if (!address) {
      setProblem(t('halls.addressFromName'));
      return;
    }
    /*
     * And the same question the server will ask, asked here first — "Support", "Help" and "About"
     * are ordinary things to call a community, and their addresses are reserved. The server still
     * refuses them; this only means the refusal arrives before the press rather than after it.
     */
    if (!isUsableSlug(address)) {
      setProblem(t('refusal.addressNotUsable'));
      return;
    }

    setProblem(null);
    setNeedsSignIn(false);
    setBusy(true);
    try {
      const hall = await client.createHall({ slug: address, name: wanted, visibility: listed ? 'public' : 'unlisted' });
      setName('');
      setSlug('');
      setSlugTouched(false);
      setMine(current => [hall, ...current]);
      /* Straight into it — making a Hall means wanting to set it up. */
      onOpen(hall.slug);
    } catch (caught) {
      if (caught instanceof SocialAuthError) setNeedsSignIn(true);
      /* The server's own words: "that address is taken", "not usable". They are the actionable
         part — and the first is one a community leader meets on their very first act here, so it
         has a name and reads in their own language. */
      else setProblem(refusalWords(t, caught, 'halls.failed'));
    } finally {
      setBusy(false);
    }
  };

  const list = (items: HallView[], emptyKey: 'halls.noneYours' | 'halls.none', couldNotRead = false): React.JSX.Element => (
    items.length === 0
      ? <p className="halls-empty" role={couldNotRead ? 'alert' : undefined}>{t(couldNotRead ? 'shared.couldNotRead' : emptyKey)}</p>
      : (
        <ul className="halls-list">
          {items.map(hall => (
            <li key={hall.id}>
              <button type="button" className="halls-open" onClick={() => onOpen(hall.slug)}>
                <span className="halls-name">{hall.name}</span>
                {hall.verified && <span className="halls-verified">{t('hall.verifiedMark')}</span>}
                {/* The size, on the row where somebody is choosing between communities — the fact
                    that decides it, and the one this list did not carry. */}
                <span className="halls-size">{t('hall.memberCount', { count: String(hall.memberCount) })}</span>
              </button>
            </li>
          ))}
        </ul>
      )
  );

  return (
    <section className="halls" aria-label={t('halls.label')}>
      <header className="halls-head">
        <h2>{t('halls.heading')}</h2>
        <button type="button" className="halls-close" onClick={onClose}>{t('hall.back')}</button>
      </header>

      {signedIn && (
        <>
          <h3>{t('halls.yours')}</h3>
          {list(mine, 'halls.noneYours', mineUnread)}
        </>
      )}

      <h3>{t('halls.public')}</h3>
      {list(halls, 'halls.none', unread)}

      <form className="halls-open-form" onSubmit={event => void open(event)}>
        <h3>{t('halls.startOne')}</h3>
        <input
          type="text"
          value={name}
          onChange={event => {
            setName(event.target.value);
            if (!slugTouched) setSlug(slugFrom(event.target.value));
          }}
          placeholder={t('halls.namePlaceholder')}
          aria-label={t('halls.namePlaceholder')}
          maxLength={HALL_LIMITS.nameMaxLength}
        />
        <input
          type="text"
          value={slugTouched ? slug : slugFrom(name)}
          onChange={event => { setSlugTouched(true); setSlug(event.target.value); }}
          placeholder={t('halls.addressPlaceholder')}
          aria-label={t('halls.addressPlaceholder')}
          maxLength={HALL_LIMITS.slugMaxLength}
        />
        {/*
          LISTED OR NOT, DECIDED HERE — because it used to be decidable only afterwards.

          `visibility` was an edit-only field, so every Hall was born in the directory: a leader
          writing the name, the address, the description and the links was discoverable half-built,
          and the way out was a second request they had to know existed. One checkbox, defaulted to
          the behaviour that was there before, so the common path costs no clicks and the quiet one
          is reachable at the moment a leader is already deciding everything else.
        */}
        <label className="halls-listed">
          <input
            type="checkbox"
            checked={listed}
            onChange={event => setListed(event.target.checked)}
          />
          {t('halls.listInDirectory')}
        </label>
        <button type="submit" disabled={busy || !name.trim()}>
          {busy ? t('halls.opening') : t('halls.startOne')}
        </button>
      </form>

      {needsSignIn && (
        <p className="halls-signin" role="alert">
          <a href={client.signInUrl()}>{t('auth.google')}</a>{t('halls.toStart')}
        </p>
      )}
      {problem && <p className="halls-problem" role="alert">{problem}</p>}
    </section>
  );
}
